import { type IValidator } from './';

export class DateValidator implements IValidator {
    status: string = '';
    statusType: 'info' | 'warning' | 'error' | 'na' = 'error';
    value?: string;
    format: string = 'y-m-d';
    formatMap?: Map<string, number>;
    yearIndex: number = 0;
    monthIndex: number = 1;
    dayIndex: number = 2;
    currentYear: number = new Date().getFullYear() - 2000;

    public constructor(format: string) {
        this.setFormat(format);
    }

    Status(): string {
        return this.status;
    }

    StatusType(): 'info' | 'warning' | 'error' | 'na' {
        return this.statusType;
    }

    Value(): string {
        return this.value as string;
    }

    private setFormat(value: string): void {
        // Remove all non-date characters and split the string into an array
        // this should always give us 3 values, if not the format is invalid. The values can contain y, yy yyy etc. but that doesnt matter.
        // we only need to know what order the values are in, and that it contains all y m d values

        // if the value contains only y, Y,m M, d, D characters we can use it as is
        const characterMap: Map<string, number> = new Map();
        if (/^[ymdYMD:]+$/.test(value)) {
            // We need to asume that the number of characters define the number of digits
            const order: string[] = [];
            const valueCharacters = value.toLowerCase().split('');
            for (const char of valueCharacters) {
                if (!characterMap.has(char)) {
                    characterMap.set(char, 1);
                    order.push(char);
                } else {
                    characterMap.set(char, characterMap.get(char)! + 1);
                }
            }

            if (order.length !== 3 || !order.includes('y') || !order.includes('m') || !order.includes('d')) {
                return;
            }

            this.formatMap = characterMap;
            value = order.join(':');
        }

        const values: string[] = value
            .toLowerCase()
            .replace(/[^dym]/g, ':')
            .split(':')
            .filter((i) => i);

        // Here we need to iterate the values and keep only 1 character per value
        // This will help us later to identify the indexes of y m d
        for (let i = 0; i < values.length; i++) {
            values[i] = values[i][0];
        }

        // We always expect 3 values and they should contain y m d
        if (values.length !== 3 || !values.includes('y') || !values.includes('m') || !values.includes('d')) {
            return;
        }

        // Find the index of the y m d values
        values.forEach((v, i) => {
            if (v.includes('y')) {
                this.yearIndex = i;
            } else if (v.includes('m')) {
                this.monthIndex = i;
            } else if (v.includes('d')) {
                this.dayIndex = i;
            }
        });

        this.format = value;
    }

    /**
     * Validatae the cell value as a date
     * @param cellvalue
     * @returns boolean
     */
    Validate(cellvalue: string): boolean {
        if (this.format === '' && !this.formatMap) {
            this.status = 'datevalidator.noformat';
            this.statusType = 'warning';
            return false;
        }

        // If we are using map, we need to format the cellvalue accordingly
        if (this.formatMap) {
            // iterate the map
            const orderedCharacters: string[] = [];
            let currentIndex = 0;
            for (const [key, characters] of this.formatMap.entries()) {
                orderedCharacters.push(cellvalue.substring(currentIndex, currentIndex + characters));
                currentIndex += characters;
            }

            cellvalue = orderedCharacters.join(':');
        }

        const _value: number[] = cellvalue
            .replace(/\D/g, ':')
            .split(':')
            .filter((i) => i)
            .map((i) => parseInt(i));

        if (_value.length !== 3) {
            this.status = 'datevalidator.invalidformat';
            this.statusType = 'error';
            return false;
        }

        // normalize values
        let year: number = _value[this.yearIndex];

        if (year < this.currentYear) {
            year += 2000;
        } else if (year < 100) {
            year += 1900;
        }

        if (
            year < 1899 ||
            year > this.currentYear + 2000 ||
            _value[this.monthIndex] < 1 ||
            _value[this.monthIndex] > 12 ||
            _value[this.dayIndex] < 1 ||
            _value[this.dayIndex] > 31
        ) {
            this.status = 'datevalidator.invaliddate';
            this.statusType = 'error';
            return false;
        }

        this.value =
            year + '-' + _value[this.monthIndex].toString().padStart(2, '0') + '-' + _value[this.dayIndex].toString().padStart(2, '0');

        return true;
    }
}
